// Support API client (SUP-01): customer chat + admin support management.
// Backend: backend/support_routes.py. Errors come back as { error, message }.
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

export class SupportError extends Error {
  constructor(status, code, message) {
    super(message || code || 'Request failed');
    this.status = status;
    this.code = code;
  }
}

export async function supportFetch(path, { method = 'GET', body } = {}) {
  let res;
  try {
    res = await fetch(`${API_BASE}/api/support${path}`, {
      method,
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new SupportError(0, 'network', 'Could not reach the server. Check your connection and try again.');
  }
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) {
    throw new SupportError(res.status, data && data.error,
      (data && data.message) || (res.status >= 500 ? 'Support is unavailable right now. Please try again shortly.' : 'Request failed.'));
  }
  return data;
}

// Random id per send so a retried request never posts the same message twice.
export function clientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const qs = (params) => {
  const p = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return p.length ? `?${p.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')}` : '';
};

// ── customer ──────────────────────────────────────────────────────────────
export const listMyConversations = () => supportFetch('/conversations');
export const startConversation = (message) =>
  supportFetch('/conversations', { method: 'POST', body: { message, client_id: clientId() } });
export const getConversation = (id, after) => supportFetch(`/conversations/${id}${qs({ after })}`);
export const sendMessage = (id, content, after) =>
  supportFetch(`/conversations/${id}/messages`, { method: 'POST', body: { content, after, client_id: clientId() } });
export const requestHuman = (id) => supportFetch(`/conversations/${id}/request-human`, { method: 'POST' });
export const closeConversation = (id) => supportFetch(`/conversations/${id}/close`, { method: 'POST' });
// 👍 1 / 👎 -1 at the end of a chat (migration 130).
export const rateConversation = (id, rating) => supportFetch(`/conversations/${id}/rate`, { method: 'POST', body: { rating } });
// "We know, we're working on it" (backend status_note.py): { note: { text, since, auto } | null }.
export const getStatusNote = () => fetch(`${API_BASE}/api/status-note`).then((r) => (r.ok ? r.json() : { note: null }))
  .then((d) => (d && d.note) || null).catch(() => null);

// ── admin ─────────────────────────────────────────────────────────────────
const A = '/admin';
export const admin = {
  dashboard: () => supportFetch(`${A}/dashboard`),
  settings: () => supportFetch(`${A}/settings`),
  saveSettings: (patch) => supportFetch(`${A}/settings`, { method: 'PUT', body: patch }),
  conversations: (state, q) => supportFetch(`${A}/conversations${qs({ state, q })}`),
  conversation: (id) => supportFetch(`${A}/conversations/${id}`),
  convAction: (id, action) => supportFetch(`${A}/conversations/${id}/${action}`, { method: 'POST' }),
  reply: (id, content) =>
    supportFetch(`${A}/conversations/${id}/reply`, { method: 'POST', body: { content, client_id: clientId() } }),
  proposeKnowledge: (id) => supportFetch(`${A}/conversations/${id}/propose-knowledge`, { method: 'POST' }),
  escalations: (status) => supportFetch(`${A}/escalations${qs({ status })}`),
  escAction: (id, action) => supportFetch(`${A}/escalations/${id}/${action}`, { method: 'POST' }),
  retryFailed: () => supportFetch(`${A}/escalations/retry-failed`, { method: 'POST' }),
  knowledge: (q, category, status) => supportFetch(`${A}/knowledge${qs({ q, category, status })}`),
  knowledgeItem: (id) => supportFetch(`${A}/knowledge/${id}`),
  createKnowledge: (body) => supportFetch(`${A}/knowledge`, { method: 'POST', body }),
  updateKnowledge: (id, body) => supportFetch(`${A}/knowledge/${id}`, { method: 'PUT', body }),
  knowledgeAction: (id, action, body) => supportFetch(`${A}/knowledge/${id}/${action}`, { method: 'POST', body: body || {} }),
  searchTest: (q) => supportFetch(`${A}/knowledge/search-test${qs({ q })}`),
  rules: () => supportFetch(`${A}/rules`),
  createRule: (body) => supportFetch(`${A}/rules`, { method: 'POST', body }),
  updateRule: (id, body) => supportFetch(`${A}/rules/${id}`, { method: 'PUT', body }),
  responses: (status, offset) => supportFetch(`${A}/responses${qs({ status, offset })}`),
  responseAction: (id, action) => supportFetch(`${A}/responses/${id}/${action}`, { method: 'POST' }),
  correct: (id, body) => supportFetch(`${A}/responses/${id}/correct`, { method: 'POST', body }),
  corrections: () => supportFetch(`${A}/corrections`),
  knowledgeFromCorrection: (id, body) => supportFetch(`${A}/corrections/${id}/knowledge`, { method: 'POST', body }),
  ruleFromCorrection: (id, body) => supportFetch(`${A}/corrections/${id}/rule`, { method: 'POST', body }),
  userBrief: (uid) => supportFetch(`${A}/users/${uid}/brief`),
  audit: (entityType, entityId) => supportFetch(`${A}/audit${qs({ entity_type: entityType, entity_id: entityId })}`),
};

export const STATE_LABELS = {
  AI_ACTIVE: 'Assistant',
  HUMAN_REQUESTED: 'Waiting for a person',
  HUMAN_ACTIVE: 'Support team',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};
